import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextDocument } from './Document';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { MediaDA } from '../../../services/admin/media.service';
import { Error } from '../../../models/error.model';
import { DocumentInbox } from '../../../models/document.inbox.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { colorStatusDocumentInbox } from '../../../scripts/badge.color.script';
import { dateFormat, datetimeFormat12h } from '../../../scripts/date.time.format.script';
import { getError } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { statusDocumentInboxValue } from '../../../libraries/status.document.inbox.library';
import { navBarShow, navBarHide } from '../../../utils/nav.bar.util';
import { assignmentEntityLabel, assignmentEntityUrl, assignmentDisplayReference } from '../../../scripts/document.assignment.script';
import { isCopilotAllowed } from '../../../scripts/copilot.auth';
import { openCopilot } from '../../../scripts/copilot.controller';
import { usePdfViewer } from '../../../contexts/PdfViewerContext';
import { attachmentPdfFilename } from '../../../scripts/document.filename.script';

import AppPreloader from '../../../components/Preloader';
import AppPdfViewer from '../../../components/element/PdfViewer';

export interface DocumentViewPageProps {};

let errorResponse: Error;

const DocumentViewPage: React.FunctionComponent<DocumentViewPageProps> = props => {
  const {asideMinimize, setAsideMinimize, setRoute, companyForUser} = UseOutletContextDocument()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()
  const { close: closeGlobalPdfViewer } = usePdfViewer()

  useEffect(() => {
    closeGlobalPdfViewer()
  }, [param.id, closeGlobalPdfViewer])

  const [mounted, setMounted] = useState(false)
  const [document, setDocument] = useState<DocumentInbox | null>(null)
  const [signedUrl, setSignedUrl] = useState<string>('')
  const [editName, setEditName] = useState<string>('')
  const [editDatedOn, setEditDatedOn] = useState<string>('')
  const [saving, setSaving] = useState<boolean>(false)
  const [vouchers, setVouchers] = useState({list: false, value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: [] as {name: boolean, file: boolean}[]})

  const executeShowReference = () => {
    navBarShow('button-navbar-aside-minimize')
    setAsideMinimize("on")
  }

  const executeHideReference = () => {
    navBarHide('button-navbar-aside-minimize')
    setAsideMinimize("off")
  }

  const loadDocument = async (id_company: string, id_document: string) => {
    await MediaDA.getDocument(id_company, id_document).then( async (response) => {
      if (response.status === 200) {
        const data = response.data
        setDocument(data)
        setEditName(data.name ?? data.original_filename ?? '')
        setEditDatedOn(data.dated_on ? new Date(data.dated_on).toISOString().slice(0, 10) : '')

        // Build vouchers state like ProviderInvoiceUpdate does
        const docId = data.id ?? data._id
        if (docId && companyForUser) {
          const url = await MediaDA.getDocumentSignedUrl(companyForUser.id, docId)
          if (url) {
            setSignedUrl(url)
            setVouchers({
              list: true,
              value: [{
                name: data.original_filename ?? data.name ?? '',
                size: data.size ?? 0,
                type: data.mime_type ?? 'application/pdf',
                view: url,
                file: url
              }],
              valid: [{ name: true, file: true }]
            })
          }
        }
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const onSubmitEdit = async () => {
    if (!companyForUser || !document) return
    const docId = document.id ?? (document as any)._id
    if (!docId) return
    setSaving(true)
    try {
      const response = await MediaDA.putDocument(companyForUser.id, docId, { name: editName, dated_on: editDatedOn || null })
      if (response.status === 200) {
        const data = response.data
        setDocument(data)
        if (data.signed_url) setSignedUrl(data.signed_url)
        setEditName(data.name ?? data.original_filename ?? '')
        setEditDatedOn(data.dated_on ? new Date(data.dated_on).toISOString().slice(0, 10) : '')

        GlobalSwal.fire({
          title: lang.labels.documentUpdated,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setSaving(false)
    }
  }

  const onDownload = async () => {
    if (!companyForUser || !document) return
    const docId = document.id ?? (document as any)._id
    if (!docId) return
    const response = await MediaDA.getDocument(companyForUser.id, docId)
    const url = response.data?.signed_url
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const submitDelete = async () => {
    if (companyForUser && document) {
      const docId = document.id ?? (document as any)._id
      GlobalSwal.fire({
        title: lang.labels.deleteDocument,
        text: lang.labels.youWantToDeleteThisDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await MediaDA.deleteDocument(companyForUser.id, docId).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyDeletedDocument,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                navigate('/main/documents/list', {replace: true})
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitArchive = async () => {
    if (companyForUser && document) {
      const docId = document.id ?? (document as any)._id
      GlobalSwal.fire({
        title: lang.labels.archiveDocument,
        text: lang.labels.youWantToArchiveThisDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await MediaDA.archiveDocument(companyForUser.id, docId).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyArchived,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                loadDocument(companyForUser.id, docId)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitIgnore = async () => {
    if (companyForUser && document) {
      const docId = document.id ?? (document as any)._id
      GlobalSwal.fire({
        title: lang.labels.ignoreDocument,
        text: lang.labels.youWantToIgnoreThisDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await MediaDA.ignoreDocument(companyForUser.id, docId).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyIgnored,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                loadDocument(companyForUser.id, docId)
              })
            } else if (response.status === 409) {
              GlobalSwal.fire({
                title: lang.labels.cannotIgnoreDocument,
                text: lang.labels.cannotIgnoreDocumentWithAssignments,
                icon: 'warning',
                confirmButtonText: lang.labels.okGotIt
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitUnignore = async () => {
    if (companyForUser && document) {
      const docId = document.id ?? (document as any)._id
      GlobalSwal.fire({
        title: lang.labels.unignoreDocument,
        text: lang.labels.youWantToUnignoreThisDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await MediaDA.unignoreDocument(companyForUser.id, docId).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyUnignored,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                loadDocument(companyForUser.id, docId)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitUnarchive = async () => {
    if (companyForUser && document) {
      const docId = document.id ?? (document as any)._id
      GlobalSwal.fire({
        title: lang.labels.unarchiveDocument,
        text: lang.labels.youWantToUnarchiveThisDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await MediaDA.unarchiveDocument(companyForUser.id, docId).then( (response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyUnarchived,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                loadDocument(companyForUser.id, docId)
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadDocument(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.documents, branch: lang.labels.document}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && document
        ?
        <>
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="card card-flush mb-5">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <div className="d-flex align-items-center gap-2">
                    <h1 className="mb-0">{document.name ?? document.original_filename}</h1>
                    {document.lifecycle === 'ignored' && (
                      <span className="badge badge-light-warning fs-7">{lang.labels.ignored}</span>
                    )}
                    {document.lifecycle === 'archived' && (
                      <span className="badge badge-light-info fs-7">{lang.labels.archives}</span>
                    )}
                  </div>
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.status}:
                    <span className={`badge badge-light-${colorStatusDocumentInbox(document.status)} text-uppercase fs-7 ms-2`}>{statusDocumentInboxValue(lang, document.status)}</span>
                  </label>
                </div>
                <div className="card-toolbar">
                  <div className="d-flex justify-content-end">
                    {isCopilotAllowed() && (
                      <button className="btn btn-light-info mb-1 me-2" type="button" onClick={() => openCopilot({ documentId: param.id, documentName: document?.name ?? document?.original_filename })}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                              <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                              <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                            </svg>
                          </span>
                          {lang.labels.copilotDiscussDocument}
                        </div>
                      </button>
                    )}
                    <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={onDownload}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 15C19 17.2 17.2 19 15 19H5C2.8 19 1 17.2 1 15C1 13.2 2.2 11.6 4 11.1C3.4 10.4 3 9.5 3 8.5C3 6.6 4.6 5 6.5 5C7.1 5 7.7 5.1 8.2 5.4C9 3.4 11 2 13.5 2C16.5 2 19 4.5 19 7.5C19 8 18.9 8.5 18.8 9C20.6 9.5 22 11.1 22 13C22 15.2 20.2 17 18 17H17" fill="black"/>
                            <path d="M12 16L7 11H10V3H14V11H17L12 16Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.downloadFile}
                      </div>
                    </button>
                    { document.status !== 'processed' &&
                      <>
                        <Dropdown>
                          <Dropdown.Toggle variant="btn btn-primary mb-1 me-2">
                            <div className="d-flex align-items-center">
                              {lang.labels.createFromDocument}
                              <span className="svg-icon svg-icon-2 me-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                </svg>
                              </span>
                            </div>
                          </Dropdown.Toggle>
                          <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-275px p-3" align="end">
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/sale/quote/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.quote}</p>
                            </Dropdown.Item>
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/sale/order/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.order}</p>
                            </Dropdown.Item>
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/sale/invoice/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.invoice}</p>
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/purchase/order/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.providerOrder}</p>
                            </Dropdown.Item>
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/purchase/invoice/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.providerInvoice}</p>
                            </Dropdown.Item>
                            <Dropdown.Item bsPrefix="menu-item" as={Link} to={`/main/purchase/expense/create?from_document=${param.id}`}>
                              <p className="menu-link fw-bold px-5 m-0">{lang.labels.expense}</p>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                        <button className="btn btn-light-danger mb-1 me-2" type="button" disabled={document.status !== 'new'} onClick={submitDelete}>
                          <div className="d-flex align-items-center">
                            {lang.labels.deleteDocument}
                          </div>
                        </button>
                        {(!document.lifecycle || document.lifecycle === 'active') && (
                          <>
                            <button className="btn btn-light-warning mb-1 me-2" type="button" onClick={submitIgnore}>
                              <div className="d-flex align-items-center">
                                {lang.labels.ignoreDocument}
                              </div>
                            </button>
                            <button className="btn btn-light-danger mb-1" type="button" onClick={submitArchive}>
                              <div className="d-flex align-items-center">
                                {lang.labels.archiveDocument}
                              </div>
                            </button>
                          </>
                        )}
                        {document.lifecycle === 'ignored' && (
                          <button className="btn btn-light-success mb-1" type="button" onClick={submitUnignore}>
                            <div className="d-flex align-items-center">
                              {lang.labels.unignoreDocument}
                            </div>
                          </button>
                        )}
                        {document.lifecycle === 'archived' && (
                          <button className="btn btn-light-success mb-1" type="button" onClick={submitUnarchive}>
                            <div className="d-flex align-items-center">
                              {lang.labels.unarchiveDocument}
                            </div>
                          </button>
                        )}
                      </>
                    }
                  </div>
                </div>
              </div>
            </div>
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">{lang.labels.documentDetails}</div>
              </div>
              <div className="card-body pt-0">
                <div className="mb-4">
                  <label className="form-label required">{lang.labels.documentName}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label">{lang.labels.documentDate}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editDatedOn}
                      onChange={(e) => setEditDatedOn(e.target.value)}
                    />
                  </div>
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSubmitEdit}
                      disabled={saving || !editName.trim()}
                    >
                      {lang.labels.save}
                    </button>
                  </div>
                </div>
              </div>

              <div className="card card-flush mb-5">
                <div className="card-body">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.status}:</td>
                        <td className="min-w-200px">
                          <span className={`badge badge-light-${colorStatusDocumentInbox(document.status)} text-uppercase fs-7`}>{statusDocumentInboxValue(lang, document.status)}</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.originalFile}:</td>
                        <td className="min-w-200px">{document.original_filename}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.uploadedOn}:</td>
                        <td className="min-w-200px">{datetimeFormat12h(lang.code, document.uploaded_at)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.assignedTo}:</td>
                        <td className="min-w-200px">
                          {(document.assignments ?? []).length > 0
                            ? (document.assignments ?? []).map((a, aIdx) => {
                                const label = assignmentEntityLabel(lang.labels, a.entity_type)
                                const ref = assignmentDisplayReference(a)
                                const url = assignmentEntityUrl(a.entity_type, a.entity_id)
                                return (
                                  <span key={aIdx}>
                                    {aIdx > 0 && ', '}
                                    <span className="text-muted">{label}: </span>
                                    {url
                                      ? <Link to={url} className="fw-bold text-primary text-hover-dark">{ref}</Link>
                                      : <span className="fw-bold">{ref}</span>
                                    }
                                  </span>
                                )
                              })
                            : lang.labels.none
                          }
                        </td>
                      </tr>
                      { document.notes &&
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.notes}:</td>
                          <td className="min-w-200px">{document.notes}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            { document && vouchers.value.length > 0 &&
              <>
                <div id="container-viewer-pdf" className={`flex-column flex-lg-row-auto order-1 order-lg-2 mb-5 mb-lg-0 ${asideMinimize === "off" && "d-none"}`}>
                  <div className="card card-flush">
                    <div className="card-header pt-3 flex-nowrap">
                      <div className="card-title">
                        <h4>{lang.labels.referencePdf}</h4>
                      </div>
                      <div className="card-toolbar d-none d-lg-flex">
                        <button className="btn btn-sm btn-icon explore-btn-dismiss me-1" type="button" title={lang.labels.download} onClick={() => {
                          const url = vouchers.value[0]?.view
                          if (!url) return
                          const link = window.document.createElement('a')
                          link.href = url
                          link.download = attachmentPdfFilename(document?.name ?? document?.original_filename, 'document')
                          window.document.body.appendChild(link)
                          link.click()
                          window.document.body.removeChild(link)
                        }}>
                          <i className="bi bi-download fs-4"></i>
                        </button>
                        <button className="btn btn-sm btn-icon explore-btn-dismiss" type="button" onClick={executeHideReference}>
                          <span className="svg-icon svg-icon-lg-2x">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                              <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      <AppPdfViewer pdf={vouchers.value[0].view}></AppPdfViewer>
                    </div>
                  </div>
                </div>
                <button className={`explore-toggle btn btn-primary shadow-lg position-fixed transform-90 rounded-top-0 fw-bolder fs-5 px-10 top-50 end-0 ${asideMinimize === "on" && "d-none"}`} type="button" style={{zIndex: 10}} onClick={executeShowReference}>
                  <span>{lang.labels.viewReferencePdf}</span>
                </button>
              </>
            }
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default DocumentViewPage;
